import { v4 as uuidv4 } from 'uuid';
import type {
  OverlayElement,
  ShapeOverlay,
  PolygonOverlay,
  TextOverlay,
  ImageOverlay,
  BlurOverlay,
  OverlayType,
  Bounds,
  RGBA,
} from '@/types';

// Counter for naming overlays
let overlayCounters: Record<string, number> = {
  rectangle: 0,
  'rounded-rectangle': 0,
  circle: 0,
  ellipse: 0,
  polygon: 0,
  text: 0,
  image: 0,
  blur: 0,
};

export const resetOverlayCounters = () => {
  Object.keys(overlayCounters).forEach((key) => {
    overlayCounters[key] = 0;
  });
};

const getNextName = (type: OverlayType): string => {
  overlayCounters[type]++;
  const typeNames: Record<OverlayType, string> = {
    rectangle: 'Rectangle',
    'rounded-rectangle': 'Rounded Rect',
    circle: 'Circle',
    ellipse: 'Ellipse',
    polygon: 'Polygon',
    text: 'Text',
    image: 'Image',
    blur: 'Blur Region',
  };
  return `${typeNames[type]} ${overlayCounters[type]}`;
};

// Default colors with FULL opacity for visibility
const defaultFillColor: RGBA = { r: 99, g: 102, b: 241, a: 1 }; // Solid indigo fill
const defaultBorderColor: RGBA = { r: 255, g: 255, b: 255, a: 1 }; // White border
const textFillColor: RGBA = { r: 255, g: 255, b: 255, a: 1 };
const blurFillColor: RGBA = { r: 0, g: 0, b: 0, a: 0.1 };

interface CreateOverlayOptions {
  bounds: Bounds;
  startTime: number;
  endTime: number;
  zIndex?: number;
}

export const createRectangleOverlay = (options: CreateOverlayOptions): ShapeOverlay => ({
  id: uuidv4(),
  type: 'rectangle',
  name: getNextName('rectangle'),
  bounds: options.bounds,
  rotation: 0,
  style: {
    fillColor: defaultFillColor,
    borderColor: defaultBorderColor,
    borderWidth: 2,
    opacity: 100,
  },
  startTime: options.startTime,
  endTime: options.endTime,
  zIndex: options.zIndex ?? 0,
  locked: false,
  visible: true,
});

export const createRoundedRectangleOverlay = (options: CreateOverlayOptions): ShapeOverlay => ({
  id: uuidv4(),
  type: 'rounded-rectangle',
  name: getNextName('rounded-rectangle'),
  bounds: options.bounds,
  rotation: 0,
  cornerRadius: 12,
  style: {
    fillColor: defaultFillColor,
    borderColor: defaultBorderColor,
    borderWidth: 2,
    opacity: 100,
  },
  startTime: options.startTime,
  endTime: options.endTime,
  zIndex: options.zIndex ?? 0,
  locked: false,
  visible: true,
});

export const createCircleOverlay = (options: CreateOverlayOptions): ShapeOverlay => ({
  id: uuidv4(),
  type: 'circle',
  name: getNextName('circle'),
  bounds: {
    ...options.bounds,
    // Make it a square for a circle
    height: options.bounds.width,
  },
  rotation: 0,
  style: {
    fillColor: defaultFillColor,
    borderColor: defaultBorderColor,
    borderWidth: 2,
    opacity: 100,
  },
  startTime: options.startTime,
  endTime: options.endTime,
  zIndex: options.zIndex ?? 0,
  locked: false,
  visible: true,
});

export const createEllipseOverlay = (options: CreateOverlayOptions): ShapeOverlay => ({
  id: uuidv4(),
  type: 'ellipse',
  name: getNextName('ellipse'),
  bounds: options.bounds,
  rotation: 0,
  style: {
    fillColor: defaultFillColor,
    borderColor: defaultBorderColor,
    borderWidth: 2,
    opacity: 100,
  },
  startTime: options.startTime,
  endTime: options.endTime,
  zIndex: options.zIndex ?? 0,
  locked: false,
  visible: true,
});

export const createPolygonOverlay = (options: CreateOverlayOptions & { sides?: number }): PolygonOverlay => {
  const sides = options.sides ?? 3;
  const centerX = options.bounds.width / 2;
  const centerY = options.bounds.height / 2;
  const radius = Math.min(options.bounds.width, options.bounds.height) / 2;
  
  // Generate points for regular polygon
  const points = [];
  for (let i = 0; i < sides; i++) {
    const angle = (i * 2 * Math.PI) / sides - Math.PI / 2;
    points.push({
      x: centerX + radius * Math.cos(angle),
      y: centerY + radius * Math.sin(angle),
    });
  }
  
  return {
    id: uuidv4(),
    type: 'polygon',
    name: getNextName('polygon'),
    bounds: options.bounds,
    rotation: 0,
    polygon: {
      points,
      sides,
    },
    style: {
      fillColor: defaultFillColor,
      borderColor: defaultBorderColor,
      borderWidth: 2,
      opacity: 100,
    },
    startTime: options.startTime,
    endTime: options.endTime,
    zIndex: options.zIndex ?? 0,
    locked: false,
    visible: true,
  };
};

export const createTextOverlay = (options: CreateOverlayOptions & { content?: string }): TextOverlay => ({
  id: uuidv4(),
  type: 'text',
  name: getNextName('text'),
  bounds: {
    ...options.bounds,
    width: Math.max(options.bounds.width, 200),
    height: Math.max(options.bounds.height, 50),
  },
  rotation: 0,
  text: {
    content: options.content ?? 'Text',
    fontFamily: 'Arial',
    fontSize: 32,
    fontWeight: 'normal',
    fontStyle: 'normal',
    textAlign: 'center',
    hasBackground: false,
    backgroundColor: { r: 0, g: 0, b: 0, a: 0.5 },
  },
  style: {
    fillColor: textFillColor,
    borderColor: { r: 0, g: 0, b: 0, a: 0 },
    borderWidth: 0,
    opacity: 100,
  },
  startTime: options.startTime,
  endTime: options.endTime,
  zIndex: options.zIndex ?? 0,
  locked: false,
  visible: true,
});

export const createImageOverlay = (
  options: CreateOverlayOptions & { src: string; originalWidth: number; originalHeight: number }
): ImageOverlay => ({
  id: uuidv4(),
  type: 'image',
  name: getNextName('image'),
  bounds: options.bounds,
  rotation: 0,
  image: {
    src: options.src,
    originalWidth: options.originalWidth,
    originalHeight: options.originalHeight,
    preserveAspectRatio: true,
  },
  style: {
    fillColor: { r: 255, g: 255, b: 255, a: 1 },
    borderColor: { r: 0, g: 0, b: 0, a: 0 },
    borderWidth: 0,
    opacity: 100,
  },
  startTime: options.startTime,
  endTime: options.endTime,
  zIndex: options.zIndex ?? 0,
  locked: false,
  visible: true,
});

export const createBlurOverlay = (options: CreateOverlayOptions): BlurOverlay => ({
  id: uuidv4(),
  type: 'blur',
  name: getNextName('blur'),
  bounds: options.bounds,
  rotation: 0,
  blur: {
    blurType: 'blur',
    intensity: 50,
  },
  style: {
    fillColor: blurFillColor,
    borderColor: { r: 99, g: 102, b: 241, a: 0.5 },
    borderWidth: 1,
    opacity: 100,
  },
  startTime: options.startTime,
  endTime: options.endTime,
  zIndex: options.zIndex ?? 0,
  locked: false,
  visible: true,
});

export const createOverlay = (
  type: OverlayType,
  options: CreateOverlayOptions & { sides?: number; content?: string; src?: string; originalWidth?: number; originalHeight?: number }
): OverlayElement => {
  switch (type) {
    case 'rectangle':
      return createRectangleOverlay(options);
    case 'rounded-rectangle':
      return createRoundedRectangleOverlay(options);
    case 'circle':
      return createCircleOverlay(options);
    case 'ellipse':
      return createEllipseOverlay(options);
    case 'polygon':
      return createPolygonOverlay({ ...options, sides: options.sides });
    case 'text':
      return createTextOverlay({ ...options, content: options.content });
    case 'image':
      if (!options.src || !options.originalWidth || !options.originalHeight) {
        throw new Error('Image overlay requires src, originalWidth, and originalHeight');
      }
      return createImageOverlay({
        ...options,
        src: options.src,
        originalWidth: options.originalWidth,
        originalHeight: options.originalHeight,
      });
    case 'blur':
      return createBlurOverlay(options);
    default:
      throw new Error(`Unknown overlay type: ${type}`);
  }
};

